import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Truck, RefreshCw, Satellite, MapPin, Gauge, Loader2, Layers } from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, LayersControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Sevilla default
const DEFAULT_CENTER = [37.3886, -5.9823];
const DEFAULT_ZOOM = 9;

// Considera online si Wialon mandó posición en la última 1h (los vehículos
// suelen estar aparcados con motor apagado pero el GPS sigue reportando cada poco)
const ONLINE_TTL_SEC = 60 * 60;

// Custom icon: rounded badge with truck silhouette
const truckIcon = (color = "#f59e0b", esMoviendo = false) => L.divIcon({
  className: "custom-truck-icon",
  html: `
    <div style="
      width: 42px; height: 42px;
      background: ${color};
      border: 3px solid #ffffff;
      border-radius: 12px;
      display: flex; align-items: center; justify-content: center;
      box-shadow: 0 6px 16px rgba(0,0,0,0.6);
      ${esMoviendo ? "animation: pulse-truck 1.5s infinite ease-out;" : ""}
    ">
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none"
        stroke="white" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M5 17h-2v-11a1 1 0 0 1 1-1h9v12m-3 0h6m4 0h2v-6h-8m0-5h5l3 5"/>
        <circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/>
      </svg>
    </div>
  `,
  iconSize: [42, 42],
  iconAnchor: [21, 21],
});

/** Determina si un vehículo está "online" basándose en la última señal. */
function esOnline(u) {
  const ts = u.ultima_posicion_ts;
  if (!ts) return false;
  const nowSec = Date.now() / 1000;
  return (nowSec - ts) <= ONLINE_TTL_SEC;
}

function getSpeed(u) {
  return Number(u.velocidad_kmh ?? u.speed ?? 0);
}

function AutoFit({ items }) {
  const map = useMap();
  useEffect(() => {
    if (!items || items.length === 0) return;
    const valid = items.filter((u) => u.lat && u.lng);
    if (valid.length === 0) return;
    if (valid.length === 1) {
      map.setView([valid[0].lat, valid[0].lng], 14);
    } else {
      const bounds = L.latLngBounds(valid.map((u) => [u.lat, u.lng]));
      map.fitBounds(bounds, { padding: [60, 60] });
    }
  }, [items, map]);
  return null;
}

export default function FlotaMapa() {
  const [units, setUnits] = useState([]);
  const [rutas, setRutas] = useState([]);  // rutas del día con paradas (para trazos)
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  const cargar = async (force = false) => {
    setRefreshing(true);
    try {
      const [uRes, rRes] = await Promise.all([
        api.get("/wialon/vehiculos", { params: force ? { force: true } : {} }),
        api.get("/rutas", { params: { fecha: new Date().toISOString().slice(0, 10) } }).catch(() => ({ data: [] })),
      ]);
      setUnits(uRes.data || []);
      setRutas(rRes.data || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Error cargando flota:", e);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  // Auto-refresh cada 30s
  useEffect(() => {
    if (!autoRefresh) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => cargar(true), 30000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [autoRefresh]);

  const valids = units.filter((u) => u.lat && u.lng);
  const moviendose = valids.filter((u) => getSpeed(u) > 5).length;
  const onlineCount = valids.filter(esOnline).length;
  const ultimaActualizacion = valids.length > 0 ? new Date().toLocaleTimeString("es-ES") : "—";

  return (
    <div className="p-4 md:p-6 max-w-[1800px] mx-auto" data-testid="page-flota-mapa">
      <style>{`
        @keyframes pulse-truck {
          0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.6); }
          70% { box-shadow: 0 0 0 14px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }
      `}</style>
      <PageHeader
        title="Mapa de flota"
        subtitle={`${valids.length} vehículos · ${onlineCount} online · ${moviendose} en movimiento · ${ultimaActualizacion}`}
        icon={Satellite}
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              data-testid="toggle-auto"
              className={`px-3 py-1.5 rounded-lg text-xs uppercase tracking-wider font-semibold transition-colors ${
                autoRefresh
                  ? "bg-emerald-500/20 text-emerald-200 border border-emerald-500/40"
                  : "bg-zinc-900 text-zinc-400 border border-zinc-800"
              }`}>
              {autoRefresh ? "Auto 30s · ON" : "Auto · OFF"}
            </button>
            <Button size="sm" onClick={() => cargar(true)} disabled={refreshing}
              data-testid="btn-refresh"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Actualizar
            </Button>
          </div>
        }
      />

      {/* KPIs flota */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard icon={Truck} label="Vehículos vinculados" value={units.length} accent="text-amber-300" />
        <StatCard icon={MapPin} label="Online (señal &lt; 1h)" value={onlineCount} accent="text-emerald-300" />
        <StatCard icon={Gauge} label="En movimiento" value={moviendose} accent="text-blue-300" />
        <StatCard icon={Satellite} label="Rutas activas hoy" value={rutas.length} accent="text-violet-300" />
      </div>

      {/* Mapa */}
      <div className="relative bg-zinc-950 border border-zinc-800 rounded-xl overflow-hidden" style={{ height: "65vh", minHeight: 500 }}>
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Cargando flota…
          </div>
        ) : (
          <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} className="h-full w-full" style={{ background: "#0a0a0c" }}>
            <LayersControl position="topright" collapsed={false}>
              <LayersControl.BaseLayer checked name="🛰️ Satélite">
                <TileLayer
                  attribution='Tiles &copy; Esri'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="🛰️ Satélite + etiquetas">
                <TileLayer
                  attribution='Tiles &copy; Esri'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="🗺️ Calles claro">
                <TileLayer
                  attribution='&copy; OpenStreetMap'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="🌃 Calles oscuro">
                <TileLayer
                  attribution='&copy; OpenStreetMap, &copy; CartoDB'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png"
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
            </LayersControl>
            <AutoFit items={valids} />
            {valids.map((u) => {
              const speed = getSpeed(u);
              const moviendo = speed > 5;
              const online = esOnline(u);
              const color = moviendo ? "#3b82f6" : (online ? "#10b981" : "#71717a");
              const tsLastSec = u.ultima_posicion_ts;
              return (
                <Marker
                  key={u.avl_id}
                  position={[u.lat, u.lng]}
                  icon={truckIcon(color, moviendo)}
                >
                  <Popup>
                    <div className="text-sm" style={{ minWidth: 220 }}>
                      <div className="font-bold text-zinc-900 mb-1">
                        🚚 {u.vehiculo_dnf?.alias || u.nombre || `Unidad ${u.avl_id}`}
                      </div>
                      {u.vehiculo_dnf?.matricula && (
                        <div className="text-xs text-zinc-600 font-mono">{u.vehiculo_dnf.matricula}</div>
                      )}
                      <hr className="my-2 border-zinc-200" />
                      <div className="space-y-1 text-xs">
                        <div><b>Velocidad:</b> {Math.round(speed)} km/h</div>
                        {tsLastSec && (
                          <div>
                            <b>Última señal:</b> {new Date(tsLastSec * 1000).toLocaleString("es-ES")}
                            <span className="ml-1 text-zinc-500">
                              (hace {Math.round((Date.now() / 1000 - tsLastSec) / 60)} min)
                            </span>
                          </div>
                        )}
                        {u.satelites != null && <div><b>Satélites:</b> {u.satelites}</div>}
                        <div className={moviendo ? "text-blue-600" : (online ? "text-emerald-600" : "text-zinc-400")}>
                          {moviendo ? "🚀 En movimiento" : (online ? "✓ Parado / online" : "⚠ Sin señal reciente")}
                        </div>
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${u.lat},${u.lng}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block text-xs text-blue-600 hover:underline"
                      >
                        Abrir en Google Maps →
                      </a>
                    </div>
                  </Popup>
                </Marker>
              );
            })}

            {/* Rutas del día: trazado entre paradas */}
            {rutas.map((r) => {
              const pts = (r.paradas || [])
                .filter((p) => p.lat && p.lng)
                .map((p) => [p.lat, p.lng]);
              if (pts.length < 2) return null;
              return (
                <Polyline
                  key={r.id}
                  positions={pts}
                  pathOptions={{
                    color: "#fbbf24",
                    weight: 4,
                    opacity: 0.85,
                    dashArray: "10 6",
                  }}
                />
              );
            })}
          </MapContainer>
        )}
      </div>

      {/* Leyenda */}
      <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-md bg-blue-500"></span> En movimiento (&gt;5 km/h)
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-md bg-emerald-500"></span> Parado / señal &lt; 1h
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-md bg-zinc-500"></span> Sin señal reciente
        </div>
        <div className="flex items-center gap-1.5">
          <span className="inline-block w-6 h-0.5 bg-amber-400" style={{ borderTop: "2px dashed" }}></span>
          Ruta del día (paradas)
        </div>
        <div className="flex items-center gap-1.5 ml-auto text-zinc-600">
          <Layers className="w-3 h-3" /> Usa el selector de capas (esquina sup. dcha) para cambiar a vista satélite
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3 flex items-center gap-3">
      <Icon className={`w-6 h-6 ${accent}`} />
      <div>
        <div className={`text-2xl font-bold ${accent}`}>{value}</div>
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      </div>
    </div>
  );
}
